import { Table, CombinedTable, Reservation, TableAvailability, TableWeekdayAvailability, TableBookablePeriod, TerracePreference } from "@/types/table";
import { isTableAvailableOnDate, isTableBookableOnDate, isTableInTemporaryBlockingPeriod } from "./tableAvailabilityUtils";
import { isTableAvailableForSlot, isCombinedTableAvailableForSlot, ConflictCheckOptions } from "./reservationConflictUtils";
import { calculateEffectiveCapacity } from "./childrenCapacityUtils";

interface AssignmentResult {
  type: 'single' | 'combined';
  id: string;
  capacity: number;
  name: string;
}

/**
 * Pour une table dépendante, retourne la capacité minimale
 * parmi toutes les combinaisons auxquelles elle appartient.
 * Plus cette valeur est élevée, moins la table bloque de combos critiques.
 */
export function getMinCombinationCapacity(
  tableId: string,
  combinedTables: CombinedTable[]
): number {
  const combos = combinedTables.filter(ct => ct.tableIds.includes(tableId));
  if (combos.length === 0) return Infinity;
  return Math.min(...combos.map(ct => ct.totalCapacity));
}

/**
 * Trie les tables dépendantes pour préserver les combinaisons critiques.
 * Préfère les tables dont les combos bloqués ont la plus grande capacité.
 * Quand customAssignmentOrderEnabled, trie d'abord par assignmentPriority ASC.
 */
function sortDependentsByComboImpact(
  tables: Table[],
  tablesInCombinations: Set<string>,
  combinedTables: CombinedTable[],
  customAssignmentOrderEnabled: boolean = false
): Table[] {
  return tables
    .filter(t => tablesInCombinations.has(t.id))
    .sort((a, b) => {
      if (customAssignmentOrderEnabled) {
        const prioDiff = (a.assignmentPriority ?? 0) - (b.assignmentPriority ?? 0);
        if (prioDiff !== 0) return prioDiff;
      }
      return getMinCombinationCapacity(b.id, combinedTables) -
        getMinCombinationCapacity(a.id, combinedTables);
    });
}

/**
 * Trie les tables indépendantes par priorité d'assignation (si activé).
 */
function sortByPriority<T extends { assignmentPriority?: number }>(
  tables: T[],
  customAssignmentOrderEnabled: boolean
): T[] {
  if (!customAssignmentOrderEnabled) return tables;
  return [...tables].sort((a, b) => (a.assignmentPriority ?? 0) - (b.assignmentPriority ?? 0));
}

/**
 * Identifie les tables qui font partie d'au moins une combinaison
 */
export function getTablesInCombinations(combinedTables: CombinedTable[]): Set<string> {
  const tablesInCombinations = new Set<string>();
  combinedTables.forEach(ct => {
    ct.tableIds.forEach(tid => tablesInCombinations.add(tid));
  });
  return tablesInCombinations;
}

/**
 * Algorithme d'assignation automatique de table OPTIMALE
 * pour les réservations côté client (/book)
 * 
 * PRIORITÉS (interleaved single/combined) :
 * 1.  Table individuelle capacité exacte
 * 1b. Table combinée capacité exacte (si >= 50%)
 * 2.  Table individuelle capacité +1
 * 2b. Table combinée capacité +1 (si >= 50%)
 * 3.  Table individuelle capacité supérieure (la plus petite)
 * 3b. Table combinée capacité supérieure (la plus petite, si >= 50%)
 * 4.  Aucune table disponible (null)
 */
export function findOptimalTable(
  numberOfPeople: number,
  date: Date,
  timeSlot: string,
  tables: Table[],
  combinedTables: CombinedTable[],
  reservations: Reservation[],
  availabilities: TableAvailability[],
  weekdayAvailabilities: TableWeekdayAvailability[],
  options?: ConflictCheckOptions,
  bookablePeriods: TableBookablePeriod[] = [],
  numberOfChildren: number = 0,
  childrenCapacityMode: 'full' | 'half' | 'none' = 'full',
  hasStroller: boolean = false,
  terracePreference: TerracePreference = 'no_preference',
  isTerraceClosedFn?: (dateStr: string, slotId?: string) => boolean,
  customAssignmentOrderEnabled: boolean = false,
  zonePreference: string[] = []
): AssignmentResult | null {
  
  // Calculer la capacité effective nécessaire
  const effectiveCapacity = calculateEffectiveCapacity(numberOfPeople, numberOfChildren, childrenCapacityMode);
  
  // Vérifier fermeture terrasse
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const terraceClosed = isTerraceClosedFn ? isTerraceClosedFn(dateStr) : false;
  
  // Si l'utilisateur veut la terrasse mais elle est fermée → aucune table
  if (terracePreference === 'terrace' && terraceClosed) return null;
  
  // Identifier les tables qui font partie de combinaisons
  const tablesInCombinations = getTablesInCombinations(combinedTables);
  
  // Filtrer les tables réservables automatiquement (exclure "hors réservation" et périodes temporaires)
  const bookableTables = tables.filter(t => {
    if (t.isBookable === false || !isTableBookableOnDate(t, date, bookablePeriods)) return false;
    // Si des enfants sont présents, exclure les tables qui n'acceptent pas les enfants
    if (numberOfChildren > 0 && t.allowsChildren === false) return false;
    // Si une poussette est présente, exclure les tables qui n'acceptent pas les poussettes
    if (hasStroller && t.allowsStroller === false) return false;
    // Si terrasse fermée et no_preference, exclure les tables terrasse
    if (terraceClosed && t.isTerrace === true) return false;
    return true;
  });

  // Terrace preference: try preferred tables first, then fallback to all
  const terraceFilter = (t: Table | CombinedTable) => {
    if (terracePreference === 'terrace') return t.isTerrace === true;
    if (terracePreference === 'indoor') return t.isTerrace !== true;
    return true;
  };

  // Filtrer aussi les tables combinées si terrasse fermée
  const effectiveCombinedTables = terraceClosed
    ? combinedTables.filter(ct => ct.isTerrace !== true)
    : combinedTables;

  // Try with preference first, then without
  const tryWithTables = (filteredTables: Table[], filteredCombined: CombinedTable[]): AssignmentResult | null => {
    return _findOptimalTableInner(
      effectiveCapacity, date, timeSlot, filteredTables, filteredCombined,
      reservations, availabilities, weekdayAvailabilities, options,
      bookablePeriods, numberOfChildren, hasStroller, tablesInCombinations, tables,
      customAssignmentOrderEnabled,
      // Composition complète : la salle/terrasse filtre ce qui est proposé,
      // jamais ce qui est occupé.
      combinedTables
    );
  };

  // Filtre par zones (strict, comme terrasse). Combinées : zone héritée si toutes les sous-tables partagent la même zone.
  const zoneFilter = (t: Table | CombinedTable) => {
    if (!zonePreference || zonePreference.length === 0) return true;
    return t.zoneId != null && zonePreference.includes(t.zoneId as string);
  };

  if (terracePreference !== 'no_preference') {
    const preferredTables = bookableTables.filter(terraceFilter).filter(zoneFilter);
    const preferredCombined = effectiveCombinedTables.filter(terraceFilter).filter(zoneFilter);
    return tryWithTables(preferredTables, preferredCombined);
  }

  return tryWithTables(bookableTables.filter(zoneFilter), effectiveCombinedTables.filter(zoneFilter));
}

/**
 * Inner assignment logic extracted for terrace preference two-pass
 */
function _findOptimalTableInner(
  effectiveCapacity: number,
  date: Date,
  timeSlot: string,
  bookableTables: Table[],
  combinedTables: CombinedTable[],
  reservations: Reservation[],
  availabilities: TableAvailability[],
  weekdayAvailabilities: TableWeekdayAvailability[],
  options?: ConflictCheckOptions,
  bookablePeriods: TableBookablePeriod[] = [],
  numberOfChildren: number = 0,
  hasStroller: boolean = false,
  tablesInCombinations: Set<string> = new Set(),
  allTables: Table[] = [],
  customAssignmentOrderEnabled: boolean = false,
  compositionTables: CombinedTable[] = combinedTables
): AssignmentResult | null {
  
  // 1. Filtrer les tables individuelles disponibles
  const availableSingleTables = bookableTables.filter(table => {
    const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
    const isAvailableDate = isTableAvailableOnDate(
      table, 
      date, 
      availabilities.filter(a => !a.isAvailable), 
      weekdayAvailability || null
    );
    const isAvailableSlot = isTableAvailableForSlot(table, date, timeSlot, reservations, compositionTables, options);
    return isAvailableDate && isAvailableSlot && table.capacity >= effectiveCapacity && effectiveCapacity >= (table.minCapacity ?? 1);
  });

  // 2. Filtrer les tables combinées disponibles (moved up for interleaved cascade)
  // Le flag is_bookable de la table combinée fait foi (pas celui des sous-tables)
  const availableCombinedTables = combinedTables.filter(ct => {
    // Vérifier le is_bookable propre de la table combinée
    if (ct.isBookable === false) return false;
    if (ct.isActive === false) return false;

    // Vérifier les contraintes enfants/poussettes de la table combinée elle-même
    if (numberOfChildren > 0 && ct.allowsChildren === false) return false;
    if (hasStroller && ct.allowsStroller === false) return false;

    // Vérifier les contraintes des sous-tables (périodes temporaires, enfants, poussettes) mais PAS leur is_bookable
    const allTablesAvailable = ct.tableIds.every(tid => {
      const table = allTables.find(t => t.id === tid);
      if (!table || table.isActive === false) return false;
      if (isTableInTemporaryBlockingPeriod(tid, date, bookablePeriods)) return false;
      if (numberOfChildren > 0 && table.allowsChildren === false) return false;
      if (hasStroller && ct.strollerPolicy !== 'allow' && table.allowsStroller === false) return false;
      return true;
    });
    if (!allTablesAvailable) return false;

    // Vérifier qu'aucune des tables de la combinaison n'a de restrictions de date
    const allTablesAvailableOnDate = ct.tableIds.every(tid => {
      const table = allTables.find(t => t.id === tid);
      if (!table || table.isActive === false) return false;
      const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === tid);
      return isTableAvailableOnDate(
        table,
        date,
        availabilities.filter(a => !a.isAvailable),
        weekdayAvailability || null
      );
    });

    if (!allTablesAvailableOnDate) return false;

    // Vérifier disponibilité pour le créneau (pas de conflit de réservation)
    const isAvailableSlot = isCombinedTableAvailableForSlot(ct, date, timeSlot, reservations, compositionTables, options);
    
    return isAvailableSlot && ct.totalCapacity >= effectiveCapacity && effectiveCapacity >= (ct.minCapacity ?? 1);
  });

  // Helper: find best combined table at a specific capacity level
  const findCombinedAtCapacity = (filterFn: (ct: CombinedTable) => boolean): AssignmentResult | null => {
    const candidates = availableCombinedTables
      .filter(ct => filterFn(ct) && effectiveCapacity >= ct.totalCapacity * 0.5)
      .sort((a, b) => {
        const capDiff = a.totalCapacity - b.totalCapacity;
        if (capDiff !== 0) return capDiff;
        if (customAssignmentOrderEnabled) {
          return (a.assignmentPriority ?? 0) - (b.assignmentPriority ?? 0);
        }
        return 0;
      });
    if (candidates.length > 0) {
      return { type: 'combined', id: candidates[0].id, capacity: candidates[0].totalCapacity, name: candidates[0].name };
    }
    return null;
  };

  // Helper: find best single table among candidates
  const findSingleAmong = (candidates: Table[]): AssignmentResult | null => {
    if (candidates.length === 0) return null;
    const independents = sortByPriority(
      candidates.filter(t => !tablesInCombinations.has(t.id)),
      customAssignmentOrderEnabled
    );
    if (independents.length > 0) {
      return { type: 'single', id: independents[0].id, capacity: independents[0].capacity, name: independents[0].name };
    }
    const sortedDependents = sortDependentsByComboImpact(candidates, tablesInCombinations, combinedTables, customAssignmentOrderEnabled);
    if (sortedDependents.length > 0) {
      const selected = sortedDependents[0];
      return { type: 'single', id: selected.id, capacity: selected.capacity, name: selected.name };
    }
    return null;
  };

  // PRIORITÉ 1 : Table individuelle capacité exacte
  const exactMatches = availableSingleTables.filter(t => t.capacity === effectiveCapacity);
  const singleExact = findSingleAmong(exactMatches);
  if (singleExact) return singleExact;

  // PRIORITÉ 1b : Table combinée capacité exacte (>= 50% rule)
  const combinedExact = findCombinedAtCapacity(ct => ct.totalCapacity === effectiveCapacity);
  if (combinedExact) return combinedExact;

  // PRIORITÉ 2 : Table individuelle capacité +1
  const plusOneMatches = availableSingleTables.filter(t => t.capacity === effectiveCapacity + 1);
  const singlePlusOne = findSingleAmong(plusOneMatches);
  if (singlePlusOne) return singlePlusOne;

  // PRIORITÉ 2b : Table combinée capacité +1
  const combinedPlusOne = findCombinedAtCapacity(ct => ct.totalCapacity === effectiveCapacity + 1);
  if (combinedPlusOne) return combinedPlusOne;

  // PRIORITÉ 3 : Table individuelle capacité supérieure (la plus petite)
  const largerTables = availableSingleTables.filter(t => t.capacity > effectiveCapacity + 1);
  
  if (largerTables.length > 0) {
    const independentTables = largerTables
      .filter(t => !tablesInCombinations.has(t.id))
      .sort((a, b) => {
        const capDiff = a.capacity - b.capacity;
        if (capDiff !== 0) return capDiff;
        if (customAssignmentOrderEnabled) {
          return (a.assignmentPriority ?? 0) - (b.assignmentPriority ?? 0);
        }
        return 0;
      });
    
    if (independentTables.length > 0) {
      const selected = independentTables[0];
      return { type: 'single', id: selected.id, capacity: selected.capacity, name: selected.name };
    }

    // Parmi les dépendantes : tri par capacité croissante, puis par priorité, puis par impact combo descendant
    const dependentTables = largerTables
      .filter(t => tablesInCombinations.has(t.id))
      .sort((a, b) => {
        const capDiff = a.capacity - b.capacity;
        if (capDiff !== 0) return capDiff;
        if (customAssignmentOrderEnabled) {
          const prioDiff = (a.assignmentPriority ?? 0) - (b.assignmentPriority ?? 0);
          if (prioDiff !== 0) return prioDiff;
        }
        return getMinCombinationCapacity(b.id, combinedTables) - getMinCombinationCapacity(a.id, combinedTables);
      });
    
    if (dependentTables.length > 0) {
      const selected = dependentTables[0];
      return { type: 'single', id: selected.id, capacity: selected.capacity, name: selected.name };
    }
  }

  // PRIORITÉ 3b : Table combinée capacité supérieure (la plus petite, >= 50%)
  const combinedLarger = findCombinedAtCapacity(ct => ct.totalCapacity > effectiveCapacity + 1);
  if (combinedLarger) return combinedLarger;

  // Aucune table disponible
  return null;
}

/**
 * Vérifie si un nombre de personnes peut être servi par au moins une table,
 * en appliquant la règle 50% pour les tables combinées.
 * 
 * @param personCount - Nombre de personnes (capacité effective, incluant enfants selon le mode)
 * @param singleTableCapacities - Capacités des tables individuelles disponibles
 * @param combinedTableRawCapacities - Capacités brutes (totalCapacity) des tables combinées disponibles
 * @param combinedTableEffectiveCapacities - Capacités effectives des tables combinées (après ajustement enfants)
 * @returns true si au moins une table peut accueillir ce nombre de personnes
 */
export function canPersonCountBeServed(
  personCount: number,
  singleTableCapacities: number[],
  combinedTableRawCapacities: number[],
  combinedTableEffectiveCapacities: number[],
  singleTableMinCapacities: number[] = [],
  combinedTableMinCapacities: number[] = [],
  effectiveOccupancyCount: number = personCount
): boolean {
  // Any single table with enough capacity and meeting min_capacity works
  if (singleTableCapacities.some((c, i) => c >= personCount && effectiveOccupancyCount >= (singleTableMinCapacities[i] ?? 1))) return true;
  // Combined: must have effective capacity >= personCount AND personCount >= 50% of raw capacity AND personCount >= minCapacity
  return combinedTableRawCapacities.some((rawCap, i) => 
    combinedTableEffectiveCapacities[i] >= personCount && 
    effectiveOccupancyCount >= Math.ceil(rawCap * 0.5) &&
    effectiveOccupancyCount >= (combinedTableMinCapacities[i] ?? 1)
  );
}

/**
 * Retourne TOUTES les tables intérieures disponibles pour un créneau donné,
 * triées par capacité croissante. Utilisé pour proposer un choix à l'utilisateur.
 */
export function findAllAvailableIndoorTables(
  numberOfPeople: number,
  date: Date,
  timeSlot: string,
  tables: Table[],
  combinedTables: CombinedTable[],
  reservations: Reservation[],
  availabilities: TableAvailability[],
  weekdayAvailabilities: TableWeekdayAvailability[],
  options?: ConflictCheckOptions,
  bookablePeriods: TableBookablePeriod[] = [],
  numberOfChildren: number = 0,
  childrenCapacityMode: 'full' | 'half' | 'none' = 'full',
  hasStroller: boolean = false,
): Array<{ type: 'single' | 'combined'; id: string; capacity: number; name: string }> {
  const effectiveCapacity = calculateEffectiveCapacity(numberOfPeople, numberOfChildren, childrenCapacityMode);
  const allTables = tables;

  // Filter indoor bookable tables
  const bookableTables = tables.filter(t => {
    if (t.isBookable === false || !isTableBookableOnDate(t, date, bookablePeriods)) return false;
    if (numberOfChildren > 0 && t.allowsChildren === false) return false;
    if (hasStroller && t.allowsStroller === false) return false;
    if (t.isTerrace === true) return false; // indoor only
    return true;
  });

  const indoorCombined = combinedTables.filter(ct => ct.isTerrace !== true);

  const results: Array<{ type: 'single' | 'combined'; id: string; capacity: number; name: string }> = [];

  // Single tables
  bookableTables.forEach(table => {
    const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
    const isAvailableDate = isTableAvailableOnDate(
      table, date,
      availabilities.filter(a => !a.isAvailable),
      weekdayAvailability || null
    );
    const isAvailableSlot = isTableAvailableForSlot(table, date, timeSlot, reservations, combinedTables, options);
    if (isAvailableDate && isAvailableSlot && table.capacity >= effectiveCapacity && effectiveCapacity >= (table.minCapacity ?? 1)) {
      results.push({ type: 'single', id: table.id, capacity: table.capacity, name: table.name });
    }
  });

  // Combined tables
  indoorCombined.forEach(ct => {
    if (ct.isBookable === false || ct.isActive === false) return;
    if (numberOfChildren > 0 && ct.allowsChildren === false) return;
    if (hasStroller && ct.allowsStroller === false) return;

    const allSubAvailable = ct.tableIds.every(tid => {
      const table = allTables.find(t => t.id === tid);
      if (!table || table.isActive === false) return false;
      if (isTableInTemporaryBlockingPeriod(tid, date, bookablePeriods)) return false;
      if (numberOfChildren > 0 && table.allowsChildren === false) return false;
      if (hasStroller && ct.strollerPolicy !== 'allow' && table.allowsStroller === false) return false;
      const wa = weekdayAvailabilities.find(w => w.tableId === tid);
      return isTableAvailableOnDate(table, date, availabilities.filter(a => !a.isAvailable), wa || null);
    });
    if (!allSubAvailable) return;

    const isAvailableSlot = isCombinedTableAvailableForSlot(ct, date, timeSlot, reservations, combinedTables, options);
    if (isAvailableSlot && ct.totalCapacity >= effectiveCapacity && effectiveCapacity >= (ct.minCapacity ?? 1)) {
      results.push({ type: 'combined', id: ct.id, capacity: ct.totalCapacity, name: ct.name });
    }
  });

  // Sort by capacity ascending
  results.sort((a, b) => a.capacity - b.capacity);
  return results;
}

/**
 * Calcule le score d'optimalité d'une table pour un nombre de personnes
 * Utilisé pour afficher des badges visuels côté staff
 * Prend en compte si la table fait partie d'une combinaison
 */
export function getOptimalityScore(
  tableCapacity: number,
  numberOfPeople: number,
  isPartOfCombination: boolean = false
): { score: number; label: string; badgeClass: string } {
  // Si la table fait partie d'une combinaison, dégrader légèrement le score
  if (tableCapacity === numberOfPeople) {
    if (isPartOfCombination) {
      return { score: 1.5, label: "Très bon", badgeClass: "bg-blue-500" };
    }
    return { score: 1, label: "Optimal", badgeClass: "bg-green-500" };
  }
  if (tableCapacity === numberOfPeople + 1) {
    if (isPartOfCombination) {
      return { score: 2.5, label: "Acceptable", badgeClass: "bg-orange-500" };
    }
    return { score: 2, label: "Très bon", badgeClass: "bg-blue-500" };
  }
  if (tableCapacity > numberOfPeople) {
    if (isPartOfCombination) {
      return { score: 3.5, label: "Sous-optimal", badgeClass: "bg-amber-600" };
    }
    return { score: 3, label: "Acceptable", badgeClass: "bg-orange-500" };
  }
  return { score: 4, label: "Sous-capacité", badgeClass: "bg-red-500" };
}
